/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useContext } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { Redirect, useMatch } from '@reach/router';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tabs from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import CodeDocs from '../../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/content/code-docs';
import ContentRenderer, {
	ContentRendererWrapper,
} from '../../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/content/content-renderer';
import { PageContext } from '../../components/page-context';
import { Tab, TabList, TabPanel, type TabProps } from '../../components/tabs';

import { getExportPropTypes } from './utils/getExportPropTypes';
import { useTabs } from './utils/use-tabs';

interface TabbedSectionProps {
	data: any;
	pageContext: { hasCodeDocs: boolean; hideChangelog?: boolean };
}

// Pull the tabs up so the TabList sits on the Hero section
const tabsContainerStyles = css({
	//Required since mainContentStyles is overriding this value for some pages (eg. icon-explorer)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&.tabs-container': {
		marginBlockStart: token('space.negative.400'),
	},
});

/**
 * __Tabbed section__
 */
const TabbedSection = ({ data, pageContext }: TabbedSectionProps) => {
	const { hasCodeDocs, hideChangelog } = pageContext;
	const changelogData = data.changelog;

	const currentPage = useContext(PageContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const tabs: TabProps[] = useTabs(
		data.mdx.nodes,
		currentPage.uri,
		hasCodeDocs,
		Boolean(changelogData) && !hideChangelog, // If changelog data exists, and there's no override to hide the changelog
	);
	const match = useMatch(`${currentPage.uri}/:tab`);
	const selectedTab = tabs.findIndex((t) => t.name === match?.tab);

	if (selectedTab < 0) {
		return <Redirect to={tabs[0].to} noThrow />;
	}

	const fireTabAnalytics = (currentTab: string, nextTab: string) => () => {
		const analyticsEvent = createAnalyticsEvent({
			actionSubject: 'tab',
			action: 'clicked',
		});

		fireUIAnalytics(analyticsEvent, 'nextTab', {
			packageName: data.workspaceInfo.name,
			currentTab,
			nextTab,
		});
	};

	const getTabContent = (tab: TabProps) => {
		if (tab.name === 'code' && hasCodeDocs) {
			return <CodeDocs workspaceInfo={data.workspaceInfo} propsMdx={data.props} />;
		}
		if (tab.name === 'changelog') {
			return changelogData ? (
				<ContentRendererWrapper>
					<ContentRenderer id={changelogData.mdx.id} body={changelogData.mdx.body} />
				</ContentRendererWrapper>
			) : null;
		}

		return (
			<ContentRendererWrapper>
				<ContentRenderer
					id={tab.content.id}
					body={tab.content.body}
					exportPropTypes={getExportPropTypes(tab, data.mdx.nodes)}
				/>
			</ContentRendererWrapper>
		);
	};

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
		<div css={tabsContainerStyles} className="tabs-container">
			<Tabs id="package-page" selected={selectedTab} shouldUnmountTabPanelOnChange>
				<TabList>
					{tabs.map((tab) => (
						<Tab
							key={tab.name}
							name={tab.name}
							to={tab.to}
							onClick={fireTabAnalytics(tab.name, tab.to)}
						/>
					))}
				</TabList>
				{tabs.map((tab) => (
					<TabPanel key={tab.name}>{getTabContent(tab)}</TabPanel>
				))}
			</Tabs>
		</div>
	);
};

export default TabbedSection;
